// re.rejouis.fr — COMMENT ON CALCULE TON CRÉDIT (page publique /prix_reprise).
// Version client du modèle de prix de reprise (exigence de transparence,
// cf. ARCHITECTURE.md §3quater). Les chiffres viennent des constantes de
// pricing.js : la page ne peut pas dériver du calcul réel.
import { EcranHeader, Icons, Illus } from '../components/ui.jsx';
import { fmtEuro } from '../lib/format.js';
import { TAUX_MIN, TAUX_PLAFOND, MALUS_ACCESSOIRES } from '../lib/pricing.js';

const pct = (t) => Math.round(t * 100) + ' %';

function Repere({ valeur, titre, texte, couleur, fond }) {
  return (
    <div className="card" style={{ padding: '16px 18px', flex: 1, minWidth: 150, borderTop: `3px solid ${couleur}` }}>
      <div className="t-display" style={{ fontSize: 26, color: couleur }}>{valeur}</div>
      <div style={{ fontWeight: 700, fontSize: 13.5, marginTop: 4 }}>{titre}</div>
      <div style={{ fontSize: 12.5, color: 'var(--encre-doux)', lineHeight: 1.55, marginTop: 4 }}>{texte}</div>
      {fond}
    </div>
  );
}

export default function PrixReprise({ go }) {
  return (
    <div className="no-scrollbar" style={{ height: '100%', overflow: 'auto', padding: '0 0 40px' }}>
      <EcranHeader titre="Comment on calcule ton crédit" onBack={() => go('accueil')} />

      <div style={{ padding: '0 18px' }}>
        <div className="soft-card" style={{ padding: '14px 16px', fontSize: 13.5, lineHeight: 1.6 }}>
          Ton crédit est un <strong>pourcentage du prix auquel on revend</strong> ton jouet
          reconditionné, dans l'état que tu déclares. Pas du prix neuf : du prix Rejouis,
          celui affiché sur la boutique.
        </div>

        {/* Les trois repères du modèle */}
        <div className="t-overline" style={{ margin: '20px 0 10px' }}>Les trois repères</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Repere
            valeur={pct(TAUX_MIN) + ' à ' + pct(TAUX_PLAFOND)}
            titre="du prix de revente"
            texte="Plus ton modèle se revend vite, plus le taux monte."
            couleur="var(--rouge)"
          />
          <Repere
            valeur="Arrondi"
            titre="à l'euro"
            texte="Pas de minimum de 5 € : tu touches ce que ton jouet vaut, au moins 1 €."
            couleur="var(--encre)"
          />
          <Repere
            valeur="Tous les prix"
            titre="sont repris"
            texte="Même un petit jouet te rapporte au moins 1 €. Tu préfères juste t'en débarrasser ? Le don reste possible."
            couleur="var(--mint-fonce)"
          />
        </div>

        {/* Ce qui fait monter le crédit */}
        <div className="t-overline" style={{ margin: '22px 0 10px' }}>Ce qui fait monter ton crédit</div>
        <div className="card" style={{ padding: '6px 18px' }}>
          {[
            { illus: 'flamme', titre: 'Un modèle qui part vite', texte: 'Plus ton modèle se revend souvent chez nous, plus le taux grimpe, jusqu’au maximum. À l’inverse, un modèle dont on a déjà des années de stock rapporte moins.' },
            { illus: 'etoile', titre: 'Les modèles recherchés', texte: 'Quand on manque d’un modèle, son crédit est boosté pour un temps limité : il porte la pastille « Boosté » et figure dans les modèles recherchés de la page d’accueil.' },
            { illus: 'don', titre: 'Ta fidélité', texte: 'Tu as déjà commandé chez Rejouis ? Connecte-toi avant d’estimer : ton crédit peut être revalorisé.' },
            { illus: 'verifie', titre: 'Un jouet complet et bien noté', texte: 'Meilleur état déclaré = variante mieux revendue = crédit plus haut. Accessoires manquants : ' + Math.round((1 - MALUS_ACCESSOIRES) * 100) + ' % de moins.' },
          ].map((l, i) => (
            <div key={l.titre} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '13px 0', borderTop: i ? '1px solid var(--bord)' : 'none' }}>
              <Illus nom={l.illus} size={40} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 13.5 }}>{l.titre}</div>
                <div style={{ fontSize: 12.5, color: 'var(--encre-doux)', lineHeight: 1.55, marginTop: 2 }}>{l.texte}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Exemple concret */}
        <div className="t-overline" style={{ margin: '22px 0 10px' }}>Un exemple</div>
        <div className="card" style={{ padding: '16px 18px', fontSize: 13, lineHeight: 1.65 }}>
          Un jouet qu'on revend <strong>85 € en « Très bon état »</strong> te rapporte entre{' '}
          <strong>{fmtEuro(Math.round(85 * TAUX_MIN))}</strong> et <strong>{fmtEuro(Math.round(85 * TAUX_PLAFOND))}</strong> de
          crédit selon la demande du moment. L'estimation exacte s'affiche avant que tu t'engages,
          et elle t'est <strong>garantie</strong> si ton jouet correspond à ta déclaration.
        </div>

        {/* Le crédit, c'est quoi */}
        <div className="soft-card" style={{ padding: '13px 16px', display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 14 }}>
          <Icons.info size={16} style={{ color: 'var(--stone)', flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 12, color: 'var(--encre-doux)', lineHeight: 1.55 }}>
            Le crédit est un <strong>avoir boutique Rejouis</strong>, versé après vérification à
            l'atelier (sous 2 jours après réception). Jouet non fonctionnel ou crédit trop faible :
            on te propose le don, et on le recycle proprement.
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 20 }}>
          <button className="btn btn-primary btn-block" onClick={() => go('accueil')}>Estimer mon jouet</button>
          <button className="btn btn-tertiary btn-block" onClick={() => go('don')}>Ou faire un don, tout simplement</button>
        </div>
      </div>
    </div>
  );
}
